// SettingsMain.tsx
import { useSearchParams } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';
import ApiIntegration from './ApiIntegration';
// import ConfigurationSettings from './ConfigurationSettings';
import { Settings, Wrench, CalendarDays, Tags, Workflow, Users, UserCog, FileText, Activity, Building2, Database, Menu, ChevronDown } from 'lucide-react';
import TagsSettings from './TagsSettings';
import ServicesMonitoring from './ServicesMonitoring';
import DbSettings from './DbSettings';
import { hasPermission } from '../../utils/permissionsHelper';
// import Jobs from './Scheduler/Jobs';
import Schedules from './Scheduler/Schedules';
import { SettingsSidebar } from './SettingsSidebar';
import Roles from '../Roles';
import User from '../User';
import EventTemplates from './EventTemplates';
import ConfigurationDetails from './ConfigurationDetails';
import { useClientLevel } from '../../hooks/useClientLevel';
// import RuleEngine from './RuleEngine/RuleEngine';
import OrganizationSettings from './OrganizationSettings';
// import CustomScripts from './CustomScripts';

export default function SettingsMain() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

    const BASE_TABS = [
        { id: 'organization_settings', label: 'Organization', component: <OrganizationSettings />, permissionKey: 'settings', icon: <Building2 size={16} /> },
        { id: 'api_integration', label: 'Integrations', component: <ApiIntegration />, permissionKey: 'integrations', icon: <Workflow size={16} /> },
        { id: 'event_templates', label: 'Templates', component: <EventTemplates />, permissionKey: 'events', icon: <FileText size={16} /> },
        // { id: 'custom_scripts', label: 'Scripts', component: <CustomScripts />, permissionKey: 'custom_scripts', icon: <Terminal size={16} /> },
        // { id: 'rule_engine', label: 'Rule Engine', component: <RuleEngine />, permissionKey: null, icon: <Brain size={16} /> },
        { id: 'services_monitoring', label: 'Services Monitoring', component: <ServicesMonitoring />, permissionKey: 'service_monitor', icon: <Activity size={16} /> },
        { id: 'db_settings', label: 'DB Monitoring', component: <DbSettings />, permissionKey: 'db_monitor', icon: <Database size={16} /> },
        // { id: 'configuration', label: 'Event Configuration', component: <ConfigurationSettings />, permissionKey: 'ticketing_event_configs', icon: <Wrench size={16} /> },
        // { id: 'jobs', label: 'Jobs', component: <Jobs />, permissionKey: 'jobs', icon: <Briefcase size={16} /> },
        { id: 'scheduler', label: 'Scheduler', component: <Schedules />, permissionKey: 'scheduler', icon: <CalendarDays size={16} /> },
        { id: 'tags', label: 'Tags', component: <TagsSettings />, permissionKey: 'tags', icon: <Tags size={16} /> },
        { id: 'users', label: 'Users', component: <User />, permissionKey: 'users', icon: <Users size={16} /> },
        { id: 'roles', label: 'Roles', component: <Roles />, permissionKey: 'roles', icon: <UserCog size={16} /> },

    ];

    const clientLevel = useClientLevel();

    const permittedTabs = useMemo(() => {
        return BASE_TABS.filter(tab => {
            // Check for level-based visibility
            if (tab.id === 'services_monitoring' && clientLevel.name === 'global') {
                return false;
            }

            if (tab.id === 'custom_scripts' && clientLevel.name === 'global') {
                return false;
            }

            if (tab.id === 'db_settings' && clientLevel.name === 'global') {
                return false;
            }


            if (tab.permissionKey) {
                return hasPermission(tab.permissionKey, 'read');
            }
            return true;
        });
    }, [clientLevel.name]);

    const requestedTab = searchParams.get('tab');
    const activeTab = useMemo(() => {
        const isValidTab = permittedTabs.some(tab => tab.id === requestedTab) || requestedTab === 'config_details';
        return isValidTab && requestedTab ? requestedTab : (permittedTabs[0]?.id || '');
    }, [requestedTab, permittedTabs]);

    const currentTabObj = useMemo(() => {
        if (activeTab === 'config_details') {
            return { id: 'config_details', label: 'Configuration Details', icon: <Wrench size={16} /> };
        }
        return permittedTabs.find(tab => tab.id === activeTab) || permittedTabs[0];
    }, [activeTab, permittedTabs]);

    useEffect(() => {
        setIsMobileDrawerOpen(false);
    }, [activeTab]);

    useEffect(() => {
        if (permittedTabs.length > 0 && requestedTab !== activeTab) {
            setSearchParams({ tab: activeTab }, { replace: true });
        }
    }, [activeTab, requestedTab, permittedTabs, setSearchParams]);

    if (permittedTabs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400 transition-colors">
                <Settings className="w-10 h-10 mb-4 opacity-50" />
                <p className="text-sm font-medium">You do not have permission to view settings.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col md:flex-row w-full bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-200 h-[calc(100vh-4rem)] overflow-hidden transition-colors">
            {/* Mobile Navigation Header & Quick Tabs */}
            <div className="md:hidden flex flex-col border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0 z-20">
                {/* Top bar with Drawer Trigger */}
                <div className="flex items-center justify-between px-3 py-2">
                    <button
                        onClick={() => setIsMobileDrawerOpen(true)}
                        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200/80 dark:bg-gray-800 dark:hover:bg-gray-700/80 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700/60 transition-colors cursor-pointer text-xs font-semibold"
                        aria-label="Open settings menu"
                    >
                        <Menu size={16} className="text-orange-500 shrink-0" />
                        <span className="flex items-center gap-1.5 truncate max-w-[210px]">
                            <span className="text-gray-500 dark:text-gray-400 font-normal">Settings /</span>
                            <span className="truncate">{currentTabObj?.label}</span>
                        </span>
                        <ChevronDown size={14} className="text-gray-400 shrink-0" />
                    </button>
                    <div className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                        {Math.max(1, permittedTabs.findIndex(t => t.id === activeTab) + 1)} / {permittedTabs.length}
                    </div>
                </div>

                {/* Horizontal Quick-Scroll Tabs */}
                <div className="flex items-center overflow-x-auto custom-scroll no-scrollbar gap-1.5 px-3 pb-2 pt-0.5">
                    {permittedTabs.map((tab) => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setSearchParams({ tab: tab.id })}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-all shrink-0 cursor-pointer ${isActive
                                    ? 'bg-orange-600 text-white shadow-sm'
                                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-gray-700'
                                    }`}
                            >
                                <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                                    {tab.icon}
                                </span>
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Sidebar - Desktop fixed left / Mobile slide-over drawer */}
            <SettingsSidebar
                tabs={permittedTabs}
                activeTab={activeTab}
                onTabChange={(id) => setSearchParams({ tab: id })}
                mobileOpen={isMobileDrawerOpen}
                onMobileClose={() => setIsMobileDrawerOpen(false)}
            />

            {/* Main Content - scrolls internally, sidebar unaffected */}
            <div className="flex-1 min-w-0 w-full bg-gray-50 dark:bg-gray-900 overflow-y-auto custom-scroll p-2 sm:p-3">
                <div className="w-full mx-auto pb-24 md:pb-12">
                    {activeTab === 'config_details' ? (
                        <ConfigurationDetails />
                    ) : (
                        permittedTabs.find(tab => tab.id === activeTab)?.component
                    )}
                </div>
            </div>
        </div>
    );
}
